/**
 * Match (Element Matching) Element Schema
 * Match-the-pairs question with text/image/audio content types
 */

import type { ElementSchema, GroupFieldSchema, ListFieldSchema, BooleanFieldSchema, TextFieldSchema } from '@ai-content-hub/content-kit';

import type { BlockContent, BlockContentType } from '../block';

import { createDefaultBlockContent, getPlainText } from '../block';

export const defaultMatchElementI18n = {
    en: {
        itemsToMatch: 'Items to Match',
        matchingOptionsHeading: 'Options',
        enterItem: 'Enter item...',
        enterOption: 'Enter option...',
        addQuestionItem: 'Add Item',
        addMatchingOption: 'Add Option',
        explanationAfterSubmit: 'Explanation (after submit)',
        explainCorrectAnswer: 'Explain the correct answer...',
        matchPlayerNoData: 'No matching data',
        matchPlayerInstruction: 'Match each item with an option',
    },
    vi: {
        itemsToMatch: 'Mục cần ghép',
        matchingOptionsHeading: 'Tùy chọn',
        enterItem: 'Nhập mục...',
        enterOption: 'Nhập đáp án...',
        addQuestionItem: 'Thêm mục',
        addMatchingOption: 'Thêm tùy chọn',
        explanationAfterSubmit: 'Giải thích (sau khi nộp)',
        explainCorrectAnswer: 'Giải thích đáp án đúng...',
        matchPlayerNoData: 'Không có dữ liệu ghép cặp',
        matchPlayerInstruction: 'Ghép mỗi mục với một đáp án',
    },
};
export interface MatchItem {
    id: string;
    rank: number;
    content: BlockContent;
    correctOptionId: string;
}

/**
 * Match option (right column - source/answer)
 */
export interface MatchOption {
    id: string;
    letter: string;
    content: BlockContent;
}

/**
 * Match Element Data
 */
export interface MatchElementData {
    items: MatchItem[];
    options: MatchOption[];
    shuffleOptions: boolean;
    oneToOne: boolean;
    explanation?: string;
}

/**
 * Answer format: itemId -> optionId
 */
export type MatchAnswer = Record<string, string>;

/**
 * Get plain text from item content
 */
export function getItemText(item: MatchItem): string {
    return getPlainText(item.content);
}

/**
 * Get plain text from option content
 */
export function getOptionText(option: MatchOption): string {
    return getPlainText(option.content);
}

/**
 * Normalize match data - ensures content has all required fields
 */
export function normalizeMatchData(data: Partial<MatchElementData>): MatchElementData {
    const items: MatchItem[] = (data.items || []).map((item, index) => {
        let content: BlockContent;
        if (item.content && typeof item.content === 'object' && 'type' in item.content) {
            content = item.content;
        } else {
            content = {
                type: 'text' as const,
                text: '',
                variant: 'body' as const,
                alignment: 'left' as const,
            };
        }
        return {
            id: item.id,
            rank: item.rank ?? index + 1,
            content,
            correctOptionId: item.correctOptionId ?? '',
        };
    });

    const options: MatchOption[] = (data.options || []).map((opt, index) => {
        let content: BlockContent;
        if (opt.content && typeof opt.content === 'object' && 'type' in opt.content) {
            content = opt.content;
        } else {
            content = {
                type: 'text' as const,
                text: '',
                variant: 'body' as const,
                alignment: 'left' as const,
            };
        }
        const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
        return {
            id: opt.id,
            letter: opt.letter ?? letters[index] ?? String.fromCharCode(65 + index),
            content,
        };
    });

    return {
        items,
        options,
        shuffleOptions: data.shuffleOptions ?? true,
        oneToOne: data.oneToOne ?? true,
        explanation: data.explanation ?? '',
    };
}

/**
 * Create default item
 */
export function createDefaultItem(rank: number): MatchItem {
    return {
        id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        rank,
        content: createDefaultBlockContent('text', { alignment: 'center' }),
        correctOptionId: '',
    };
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

/**
 * Create default option
 */
export function createDefaultOption(index: number): MatchOption {
    const letter = OPTION_LETTERS[index] ?? String.fromCharCode(65 + index);
    return {
        id: `opt-${letter}-${Date.now()}`,
        letter,
        content: createDefaultBlockContent('text', { alignment: 'center' }),
    };
}

export const matchElementSchema: ElementSchema<MatchElementData> = {
    type: 'element-match',
    title: 'Element Matching',
    description: 'Match items on the left with options on the right. Supports text, image, and audio content.',
    version: '1.0.0',
    category: 'question',
    icon: 'Link2',
    gradable: true,
    tags: ['match', 'matching', 'question', 'assessment', 'drag-drop'],
    fields: [
        {
            name: 'items',
            type: 'group',
            label: 'Items to Match',
            description: 'Items on the left that users match with options',
            required: true,
        } as GroupFieldSchema,
        {
            name: 'options',
            type: 'list',
            label: 'Options',
            description: 'Answer options A, B, C, D... (can be more than items)',
            required: true,
            minItems: 2,
            maxItems: 12,
        } as ListFieldSchema,
        {
            name: 'shuffleOptions',
            type: 'boolean',
            label: 'Shuffle Options',
            description: 'Randomize option order in player',
            defaultValue: true,
        } as BooleanFieldSchema,
        {
            name: 'oneToOne',
            type: 'boolean',
            label: 'One-to-One Matching',
            description: 'Each option can only be used once',
            defaultValue: true,
        } as BooleanFieldSchema,
        {
            name: 'explanation',
            type: 'text',
            label: 'Explanation',
            description: 'Explanation shown after answer',
            placeholder: 'Explain the correct answer...',
            multiline: true,
        } as TextFieldSchema,
    ],
    defaultData: {
        items: [
            {
                id: 'item-1',
                rank: 1,
                content: createDefaultBlockContent('text'),
                correctOptionId: '',
            },
            {
                id: 'item-2',
                rank: 2,
                content: createDefaultBlockContent('text'),
                correctOptionId: '',
            },
        ],
        options: [
            { id: 'opt-A', letter: 'A', content: createDefaultBlockContent('text') },
            { id: 'opt-B', letter: 'B', content: createDefaultBlockContent('text') },
        ],
        shuffleOptions: true,
        oneToOne: true,
        explanation: '',
    },
    i18n: defaultMatchElementI18n,
};
